import React from 'react';
import { Box, Grid, Paper, Stack } from '@mui/material';
import Typography from '../i18n/LocalizedTypography';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { useTranslation } from 'react-i18next';

export interface PanchangAlmanacData {
  date: string;
  day?: string;
  tithi?: string;
  nakshatra?: string;
  yoga?: string;
  karana?: string;
  sunrise?: string;
  sunset?: string;
  moonrise?: string;
  moonset?: string;
  paksha?: string;
  rashi?: {
    moonSign?: string;
    sunSign?: string;
  };
  calendar?: {
    vikramSamvat?: number;
    shakaSamvat?: number;
    gujaratiSamvat?: number;
    vedicMonth?: {
      name?: string;
      displayName?: string;
      localized?: Record<string, string>;
      labelLocalized?: Record<string, string>;
      isAdhika?: boolean;
      system?: string;
    };
    ayana?: string;
    ritu?: string;
  };
  timings?: {
    auspicious?: Array<{ name: string; value: string }>;
    inauspicious?: Array<{ name: string; value: string }>;
  };
  details?: {
    tithiNumber?: number;
    nakshatraNumber?: number;
    nakshatraPada?: number;
    yogaNumber?: number;
    karanaNumber?: number;
  };
  panchangAnalysis?: {
    status?: 'supportive' | 'mixed' | 'caution';
    summary?: { en?: string; hi?: string };
    disclaimer?: { en?: string; hi?: string };
    labels?: Record<string, { sectionLords?: string; sectionReview?: string; planetaryRuler?: string; deity?: string; summary?: string; guidance?: string; importantNote?: string }>;
    limbs?: Array<{ key: string; name: string; planetaryRuler?: string; alternatePlanetaryRuler?: string; deity?: string; nature?: string; guidance?: Record<string, string>; explanation?: Record<string, string>; nameLocalized?: Record<string, string>; planetaryRulerLocalized?: Record<string, string>; alternatePlanetaryRulerLocalized?: Record<string, string> | null; deityLocalized?: Record<string, string> }>;
    cautions?: Array<{ key: string; name: string; explanation?: { en?: string; hi?: string } }>;
  };
}

interface PanchangAlmanacProps {
  panchang: PanchangAlmanacData;
  locationLabel?: string;
  compact?: boolean;
}

const theme = {
  page: '#f7fbf8',
  pageLight: '#e8f4ed',
  section: '#dcefe5',
  border: '#b8d8c5',
  text: '#1d3928',
  deep: '#237447',
  highlight: '#eff8f2',
  accent: '#2e7d4f',
};

const parseDate = (value: string) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatFullDate = (value: string) => {
  return formatDisplayDate(parseDate(value));
};

const splitRows = (rows: Array<[string, React.ReactNode]>) => {
  const pairs: Array<Array<[string, React.ReactNode]>> = [];
  for (let i = 0; i < rows.length; i += 2) pairs.push(rows.slice(i, i + 2));
  return pairs;
};

const toTimingRows = (items: Array<{ name: string; value: string }>): Array<[string, React.ReactNode]> => (
  items.map((item) => [item.name, item.value])
);

function Section({ title, rows }: { title: string; rows: Array<[string, React.ReactNode]> }) {
  return (
    <Box sx={{ border: `1px solid ${theme.border}`, borderTop: 0 }}>
      <Box sx={{ bgcolor: theme.section, color: theme.deep, px: 1.5, py: 0.8, fontWeight: 600, textAlign: 'left', fontSize: 14 }}>
        {title}
      </Box>
      {splitRows(rows).map((pair, index) => (
        <Grid container key={`${title}-${index}`} sx={{ borderTop: `1px solid ${theme.border}` }}>
          {pair.map(([label, value]) => (
            <Grid size={{ xs: 12, sm: 6 }} key={label}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', minHeight: 38, borderRight: { sm: `1px solid ${theme.border}` }, bgcolor: '#fff' }}>
                <Typography sx={{ px: 1.2, py: 0.9, fontWeight: 600, color: theme.deep, fontSize: 13 }}>
                  {label}
                </Typography>
                <Typography sx={{ px: 1.2, py: 0.9, color: theme.text, fontSize: 13, fontWeight: 600 }}>
                  {value || '—'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
}

export default function PanchangAlmanac({ panchang, locationLabel, compact = false }: PanchangAlmanacProps) {
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || 'en').split(/[-_]/)[0];
  const date = parseDate(panchang.date);
  const selectedDay = date.getDate();
  const auspicious = panchang.timings?.auspicious || [];
  const inauspicious = panchang.timings?.inauspicious || [];
  const analysis = panchang.panchangAnalysis;
  const localized = (value?: Record<string, string>) => value?.[language] || value?.en || '—';
  const labels = analysis?.labels?.[language] || analysis?.labels?.en || {};
  const vedicMonth = panchang.calendar?.vedicMonth;
  const vedicMonthName = vedicMonth?.localized?.[language] || vedicMonth?.localized?.en || vedicMonth?.displayName || vedicMonth?.name || '—';
  const vedicMonthLabel = vedicMonth?.labelLocalized?.[language] || vedicMonth?.labelLocalized?.en || 'Vedic Month';
  const rulerRows: Array<[string, React.ReactNode]> = (analysis?.limbs || []).map((limb) => [localized(limb.nameLocalized) || limb.name, `${labels.planetaryRuler || 'Planetary ruler'}: ${localized(limb.planetaryRulerLocalized) || limb.planetaryRuler || '—'}${limb.alternatePlanetaryRuler ? ` / ${localized(limb.alternatePlanetaryRulerLocalized || undefined)}` : ''} · ${labels.deity || 'Deity'}: ${localized(limb.deityLocalized) || limb.deity || '—'}`]);
  const reviewRows: Array<[string, React.ReactNode]> = [
    [labels.summary || 'Summary', localized(analysis?.summary as Record<string, string>)],
    ...((analysis?.cautions || []).map((item) => [localized((analysis?.limbs || []).find((limb) => limb.key === item.key)?.nameLocalized) || item.name, localized(item.explanation as Record<string, string>)] as [string, React.ReactNode])),
    ...((analysis?.limbs || []).filter((item) => item.guidance).map((item) => [`${localized(item.nameLocalized) || item.name} ${labels.guidance || 'Guidance'}`, localized(item.guidance)] as [string, React.ReactNode])),
    [labels.importantNote || 'Important note', localized(analysis?.disclaimer as Record<string, string>)],
  ];

  return (
    <Paper
      elevation={compact ? 0 : 2}
      sx={{
        overflow: 'hidden',
        borderRadius: 1,
        border: `1px solid ${theme.border}`,
        bgcolor: theme.page,
        color: theme.text,
        boxShadow: compact ? 'none' : '0 14px 34px rgba(35, 116, 71, 0.10)',
      }}
    >
      <Box sx={{ bgcolor: theme.accent, color: '#fff', px: 2, py: 1, fontWeight: 600 }}>
        {formatFullDate(panchang.date)}
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2, background: 'linear-gradient(135deg, #edf8f1 0%, #d8efe2 52%, #c5e3d2 100%)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: theme.deep }}>
              {panchang.details?.nakshatraNumber ? `${String(panchang.details.nakshatraNumber).padStart(2, '0')}, ` : ''}
              {panchang.nakshatra || 'Nakshatra'}
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {vedicMonthName} · {panchang.paksha || 'Paksha'}, {panchang.tithi || 'Tithi'}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              {locationLabel || 'Selected location'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ textAlign: { xs: 'left', md: 'right' }, fontWeight: 600, textDecoration: 'underline' }}>
              {selectedDay.toString().padStart(2, '0')}
            </Typography>
            <Typography sx={{ textAlign: { xs: 'left', md: 'right' }, fontSize: 13, fontWeight: 600 }}>
              {formatFullDate(panchang.date)} · {panchang.day || 'Vara'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 1.25, py: 1.5, bgcolor: '#fff', borderY: `1px solid ${theme.border}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ fontSize: 13, fontWeight: 600 }}>
          <span>Sunrise: {panchang.sunrise || '—'}</span>
          <span>Moonrise: {panchang.moonrise || '—'}</span>
          <span>Tithi: {panchang.tithi || '—'}</span>
          <span>Nakshatra: {panchang.nakshatra || '—'}</span>
          <span>Sunset: {panchang.sunset || '—'}</span>
        </Stack>
      </Box>

      <Section
        title="Sunrise and Moonrise"
        rows={[
          ['Sunrise', panchang.sunrise],
          ['Sunset', panchang.sunset],
          ['Moonrise', panchang.moonrise],
          ['Moonset', panchang.moonset],
        ]}
      />

      <Section
        title="Panchang"
        rows={[
          ['Tithi', `${panchang.tithi || '—'}${panchang.details?.tithiNumber ? ` (#${panchang.details.tithiNumber})` : ''}`],
          ['Nakshatra', `${panchang.nakshatra || '—'}${panchang.details?.nakshatraPada ? ` Pada ${panchang.details.nakshatraPada}` : ''}`],
          ['Yoga', `${panchang.yoga || '—'}${panchang.details?.yogaNumber ? ` (#${panchang.details.yogaNumber})` : ''}`],
          ['Karana', `${panchang.karana || '—'}${panchang.details?.karanaNumber ? ` (#${panchang.details.karanaNumber})` : ''}`],
          ['Weekday', panchang.day],
          ['Paksha', panchang.paksha],
        ]}
      />

      {rulerRows.length > 0 && <Section title={labels.sectionLords || 'Panchang Lords and Deities'} rows={rulerRows} />}
      {analysis && <Section title={labels.sectionReview || 'Panchang Quality Review'} rows={reviewRows} />}

      <Section
        title="Lunar Month, Samvat and Rashi"
        rows={[
          [vedicMonthLabel, vedicMonthName],
          ['Vikram Samvat', panchang.calendar?.vikramSamvat],
          ['Shaka Samvat', panchang.calendar?.shakaSamvat],
          ['Gujarati Samvat', panchang.calendar?.gujaratiSamvat],
          ['Moon Sign', panchang.rashi?.moonSign],
          ['Sun Sign', panchang.rashi?.sunSign],
          ['Nakshatra Pada', panchang.details?.nakshatraPada],
        ]}
      />

      <Section
        title="Ritu and Ayana"
        rows={[
          ['Vedic Ritu', panchang.calendar?.ritu],
          ['Vedic Ayana', panchang.calendar?.ayana],
          ['Sunrise Source', 'Location based calculation'],
          ['Panchang Source', 'Sidereal longitude calculations'],
        ]}
      />

      <Section
        title="Auspicious Timings"
        rows={toTimingRows(auspicious.length ? auspicious : [{ name: 'Abhijit Muhurta', value: '—' }])}
      />

      <Section
        title="Inauspicious Timings"
        rows={toTimingRows(inauspicious.length ? inauspicious : [{ name: 'Rahu Kalam', value: '—' }])}
      />

      <Box sx={{ p: 1.25, bgcolor: theme.highlight, borderTop: `1px solid ${theme.border}` }}>
        <Typography sx={{ fontSize: 12, color: theme.deep, fontWeight: 600 }}>
          Panchang limbs are calculated for sunrise at the selected place. Advanced end times for each limb can be added when high-precision transition calculations are enabled.
        </Typography>
      </Box>
    </Paper>
  );
}
